/**
 * Device Type Detail Component
 *
 * Read-only detail view for a single device type.
 * Mirrors the OrganizationDetail look: back-button row with an Edit button,
 * a dark ProfileHeader hero, and dark ProfileSection cards.
 */

import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ProfileHeader, ProfileSection } from '@/components/sensnet/ProfileLayout'
import { ArrowLeft, Loader2, Edit, Cpu, Settings2 } from 'lucide-react'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'

export interface DeviceTypeDetailProps {
  deviceTypeNumber: number
}

export function DeviceTypeDetail({ deviceTypeNumber }: DeviceTypeDetailProps) {
  const navigate = useNavigate()

  const { data, isLoading } = useApiQuery<any>('getDeviceType', { deviceTypeNumber })
  const deviceType = data?.content?.deviceType

  const handleBack = () => navigate({ to: '/sensory/device-types' })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading device type details...</span>
        </div>
      </div>
    )
  }

  if (!deviceType) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Device Type not found.</p>
        <Button variant="outline" onClick={handleBack}>Back to Device Types</Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link
            to="/sensory/device-types/$deviceTypeNumber/edit"
            params={{ deviceTypeNumber: String(deviceType.deviceTypeNumber) }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={deviceType.deviceTypeName}
        subtitle={`Device Type Number: ${deviceType.deviceTypeNumber}`}
        subtitleIcon={<Cpu className="h-4 w-4" />}
        badge={deviceType.deviceTypeStatus}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ProfileSection title="General Information" icon={<Cpu className="h-4 w-4" />} variant="dark">
          <div className="space-y-3 p-5">
            <div>
              <p className="text-xs text-muted-foreground">Device Type Name</p>
              <p className="text-sm">{deviceType.deviceTypeName}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Device Type Number</p>
              <p className="text-sm">{deviceType.deviceTypeNumber}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Manufacturer Number</p>
              <p className="text-sm">{deviceType.deviceTypeManufacturerNumber ?? '—'}</p>
            </div>
          </div>
        </ProfileSection>

        <ProfileSection title="Configuration" icon={<Settings2 className="h-4 w-4" />} variant="dark">
          <div className="space-y-3 p-5">
            <div>
              <p className="text-xs text-muted-foreground">Model</p>
              <p className="text-sm">{deviceType.deviceTypeModel || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Category</p>
              <p className="text-sm">{deviceType.deviceTypeCategory || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Status</p>
              <p className="text-sm">{deviceType.deviceTypeStatus || '—'}</p>
            </div>
          </div>
        </ProfileSection>
      </div>
    </div>
  )
}
